/**
 * Work pattern — the employee's weekly off days and half days, versioned by start date.
 * Backend: GET/PUT/DELETE /api/attendance/work-patterns/{employee} (HR; reason required; audited).
 * Attendance (off days, half-day base) and leave day counting read it; without a version the company
 * weekend applies, and every screen labels that as the company default, not the contract.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarClock, Loader2, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import ReasonDialog from '@/pages/attendance/ReasonDialog';
import { api, errorText } from '@/pages/attendance/shared';

const NAMES = { en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  ar: ['الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت', 'الأحد'] };
const KIND = { work: ['Work', 'عمل'], off: ['Off', 'راحة'], half: ['Half day', 'نصف يوم'] };

const describe = (days, isRTL) => {
  const n = NAMES[isRTL ? 'ar' : 'en'];
  const off = days.map((d, i) => (d.kind === 'off' ? n[i] : null)).filter(Boolean);
  const half = days.map((d, i) => (d.kind === 'half' ? `${n[i]} ${d.minutes / 60}${isRTL ? 'س' : 'h'}` : null)).filter(Boolean);
  const sep = isRTL ? '، ' : ', ';
  return `${isRTL ? 'راحة' : 'Off'}: ${off.join(sep) || (isRTL ? 'لا يوجد' : 'none')}${half.length ? ` · ${isRTL ? 'نصف يوم' : 'half day'}: ${half.join(sep)}` : ''}`;
};

export default function WorkPatternTab({ token, employeeId, isRTL }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null);        // {effective_from, days}
  const [removing, setRemoving] = useState(null);  // effective_from

  const load = useCallback(() => {
    setError('');
    api(token).get(`/work-patterns/${employeeId}`).then(setData).catch((e) => setError(errorText(e)));
  }, [token, employeeId]);
  useEffect(() => { load(); }, [load]);

  const startEdit = (days) => setDraft({ effective_from: new Date().toISOString().slice(0, 10),
    days: days.map((d) => ({ kind: d.kind, minutes: d.minutes ?? '' })) });

  const save = async (reason) => {
    const days = draft.days.map((d) => ({ kind: d.kind, minutes: d.kind === 'off' || d.minutes === '' ? null : Number(d.minutes) }));
    if (days.some((d) => d.kind === 'half' && !d.minutes)) { toast.error(t('A half day needs its minutes', 'نصف اليوم يحتاج عدد دقائقه')); return; }
    try {
      await api(token).put(`/work-patterns/${employeeId}`, { effective_from: draft.effective_from, days, reason });
      toast.success(t('Work pattern saved', 'تم حفظ نمط العمل')); setDraft(null); load();
    } catch (e) { toast.error(errorText(e)); }
  };
  const remove = async (reason) => {
    try {
      await api(token).del(`/work-patterns/${employeeId}/${removing}`, { reason });
      toast.success(t('Removed', 'تم الحذف')); setRemoving(null); load();
    } catch (e) { toast.error(errorText(e)); }
  };

  if (error) return <div className="text-sm text-rose-600">{error}</div>;
  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  const weekendNames = data.company_weekend.map((d) => NAMES[isRTL ? 'ar' : 'en'][d]).join(isRTL ? '، ' : ', ') || t('none', 'لا يوجد');

  return (
    <Card data-testid="work-pattern-tab">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <CalendarClock className="h-4 w-4" />{t('Weekly work pattern', 'نمط العمل الأسبوعي')}
          {data.source === 'profile'
            ? <Badge variant="outline">{t('from profile', 'من ملف الموظف')}</Badge>
            : <Badge variant="outline" className="text-amber-800 border-amber-300 bg-amber-50">{t('company default, not from profile', 'الافتراضي للشركة، ليس من الملف')}</Badge>}
          <Button size="sm" variant="outline" className="ms-auto" onClick={() => startEdit(data.days)} data-testid="work-pattern-edit">
            <Plus className="h-4 w-4 me-1" />{t('New pattern from a date', 'نمط جديد من تاريخ')}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div data-testid="work-pattern-current">
          {data.source === 'profile' ? describe(data.days, isRTL)
            : t(`No pattern on the profile: attendance and leave use the company weekend (${weekendNames}).`,
              `لا يوجد نمط في الملف: يستخدم الحضور والإجازات عطلة الشركة (${weekendNames}).`)}
        </div>
        {data.work_schedule_hint && (
          <div className="text-xs bg-slate-50 border rounded p-2 flex flex-wrap items-center gap-2">
            {t('The free-text work schedule reads as', 'يُقرأ جدول العمل النصي كالتالي')}: <b>{describe(data.work_schedule_hint.days, isRTL)}</b>
            <span className="text-slate-500">{t('(not applied — confirm it to use it)', '(غير مطبَّق — أكِّده لاستخدامه)')}</span>
            <Button size="sm" variant="ghost" onClick={() => startEdit(data.work_schedule_hint.days)}>{t('Use it', 'استخدامه')}</Button>
          </div>
        )}
        {data.versions.length > 0 && (
          <table className="w-full text-xs border-collapse">
            <thead><tr className="text-slate-500">
              <th className="text-start py-1">{t('From', 'من')}</th><th className="text-start">{t('Pattern', 'النمط')}</th>
              <th className="text-start">{t('Source', 'المصدر')}</th><th className="text-start">{t('Reason', 'السبب')}</th><th />
            </tr></thead>
            <tbody>
              {[...data.versions].reverse().map((v) => (
                <tr key={v.effective_from} className="border-t">
                  <td className="py-1 font-mono">{v.effective_from}</td>
                  <td>{describe(v.days, isRTL)}</td>
                  <td>{v.source === 'import' ? t('import', 'استيراد') : t('profile', 'الملف')}</td>
                  <td className="text-slate-500">{v.reason}</td>
                  <td className="text-end">
                    <Button size="sm" variant="ghost" onClick={() => setRemoving(v.effective_from)} title={t('Remove', 'حذف')}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>

      <ReasonDialog open={!!draft} isRTL={isRTL} title={t('Weekly work pattern from', 'نمط العمل الأسبوعي من')}
        description={t('Applies from this date on; earlier periods keep their pattern. A date inside a final pay period is refused.',
          'يسري من هذا التاريخ؛ تحتفظ الفترات السابقة بنمطها. يُرفض تاريخ داخل فترة راتب نهائية.')}
        onCancel={() => setDraft(null)} onConfirm={save}>
        {draft && (
          <div className="space-y-2 text-sm">
            <Input type="date" value={draft.effective_from} onChange={(e) => setDraft({ ...draft, effective_from: e.target.value })}
              data-testid="work-pattern-from" />
            {draft.days.map((d, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="w-24">{NAMES[isRTL ? 'ar' : 'en'][i]}</span>
                <select className="border rounded-md px-2 py-1" value={d.kind} data-testid={`work-pattern-kind-${i}`}
                  onChange={(e) => setDraft({ ...draft, days: draft.days.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)) })}>
                  {Object.entries(KIND).map(([k, [en, ar]]) => <option key={k} value={k}>{isRTL ? ar : en}</option>)}
                </select>
                {d.kind !== 'off' && (
                  <Input type="number" min="30" max="960" className="w-28 h-8" value={d.minutes}
                    placeholder={d.kind === 'half' ? t('minutes', 'دقائق') : t('base', 'الأساس')}
                    onChange={(e) => setDraft({ ...draft, days: draft.days.map((x, j) => (j === i ? { ...x, minutes: e.target.value } : x)) })} />
                )}
              </div>
            ))}
            <p className="text-xs text-slate-500">{t('Work with no minutes uses the schedule profile’s base (8 h office, 10 h two-shift).',
              'العمل بلا دقائق يستخدم أساس ملف الدوام (8 ساعات مكتبي، 10 ساعات فترتين).')}</p>
          </div>
        )}
      </ReasonDialog>
      <ReasonDialog open={!!removing} isRTL={isRTL} destructive title={t('Remove the pattern from', 'حذف النمط من') + ` ${removing || ''}`}
        description={t('The earlier pattern, or the company weekend, applies again from that date.', 'يعود النمط السابق، أو عطلة الشركة، من ذلك التاريخ.')}
        onCancel={() => setRemoving(null)} onConfirm={remove} />
    </Card>
  );
}
